import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import { MANUAL_TYPE } from "constants/opportunities";
import { CAPTURE_TRACKED_OPPS_ROOT } from "constants/routes";
import useShowArchiveEntityModal from "hooks/useShowArchiveEntityModal";
import OPP_EDIT_PANEL_CONFIG from "layout/opportunities/EditOpportunityPanel/helpers";
import { Archive, ArchiveRestore, SquarePen } from "lucide-react";
import UntrackButton from "pages/capture/TrackedOpportunities/UntrackButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setShowEditModal } from "slices/opportunity/opportunities/opportunitiesSlice";
import { selectOpportunity } from "slices/opportunity/summary/selectors";
import {
  archiveOpportunity,
  unarchiveOpportunity,
} from "slices/opportunity/tracked/actions";

import classes from "./Options.module.scss";

const Options = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showArchiveEntityModal = useShowArchiveEntityModal();
  const opportunity = useSelector(selectOpportunity);
  const opportunityId = opportunity?.id;
  const opportunityTitle = opportunity?.title;
  const isManualOpp = opportunity?.opportunitySource === MANUAL_TYPE;
  const isEditableOpp = Boolean(OPP_EDIT_PANEL_CONFIG[opportunity?.opportunitySource]);
  const isArchivedOpp = !!opportunity?.archivedAt;

  if (!opportunity) return null;

  const isProposal = opportunity.proposalStarted;

  const handleArchive = () => {
    showArchiveEntityModal({
      primarySlot: { entityType: "tracked-opportunities", label: "Tracked Opportunity" },
      entityName: opportunityTitle,
      cascadeWarning: isProposal ? (
        <>
          Archiving this opportunity will also archive its <strong>related proposal</strong> as
          well.
        </>
      ) : undefined,
      onConfirm: async ({ statusIdsBySlot, note }) => {
        try {
          await dispatch(
            archiveOpportunity(opportunityId, {
              archiveStatusId: statusIdsBySlot.primary,
              archiveNote: note,
            })
          );
          navigate(CAPTURE_TRACKED_OPPS_ROOT);
        } catch {
          // handled by archiveOpportunity (error/locked-items modal); stay on this page
        }
      },
    });
  };

  const handleUnarchive = async () => {
    await dispatch(unarchiveOpportunity(opportunityId));
    navigate("/capture/archived-opportunities");
  };

  return (
    <div className={classes["options"]}>
      {isArchivedOpp ? (
        <NewButton
          variant={NewButtonVariant.l1_5}
          rightIcon={<ArchiveRestore size={14} />}
          onClick={handleUnarchive}
        >
          Unarchive
        </NewButton>
      ) : (
        <NewButton
          variant={NewButtonVariant.l2}
          rightIcon={<Archive size={14} />}
          onClick={handleArchive}
        >
          Archive
        </NewButton>
      )}
      {!isArchivedOpp && isEditableOpp && (
        <NewButton
          variant={NewButtonVariant.l2}
          onClick={() => dispatch(setShowEditModal(true))}
          leftIcon={<SquarePen size={14} />}
        >
          Edit
        </NewButton>
      )}

      <UntrackButton
        isManualOpp={isManualOpp}
        opportunityId={opportunityId}
        opportunityTitle={opportunityTitle}
        fromSummary={true}
        asButton={true}
      />
    </div>
  );
};

export default Options;
